import type { FC, ReactElement } from 'react';
import { useState, useEffect, useMemo } from 'react';
import { NavGroup, NavList } from '@patternfly/react-core';
import { css } from '@patternfly/react-styles';
import { useTranslation } from 'react-i18next';
import {
  useActivePerspective,
  isNavSection,
  isNavItem,
} from '@console/dynamic-plugin-sdk/src/lib-core';
import withDragDropContext from '@console/internal/components/utils/drag-drop-context';
import { modelFor } from '@console/internal/module/k8s';
import { usePinnedResources } from '@console/shared/src/hooks/usePinnedResources';
import PinnedResource from './PinnedResource';
import { PluginNavItem } from './PluginNavItem';
import { useNavExtensionsForPerspective } from './useNavExtensionForPerspective';
import { getSortedNavExtensions, isTopLevelNavItem } from './utils';

import './PerspectiveNav.scss';

const PerspectiveNav: FC<{}> = () => {
  const [activePerspective] = useActivePerspective();
  const allNavExtensions = useNavExtensionsForPerspective(activePerspective);
  const [pinnedResources, setPinnedResources, pinnedResourcesLoaded] = usePinnedResources();
  const [validPinnedResources, setValidPinnedResources] = useState<string[]>([]);
  const [isDragged, setIsDragged] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const validResources = pinnedResources.filter((res) => !!modelFor(res));
    setValidPinnedResources(validResources);
  }, [setValidPinnedResources, pinnedResources]);

  const orderedNavExtensions = useMemo(() => {
    const topLevelNavExtensions = allNavExtensions.filter(isTopLevelNavItem);
    return getSortedNavExtensions(topLevelNavExtensions);
  }, [allNavExtensions]);

  const getPinnedItems = (): ReactElement[] =>
    validPinnedResources.map((resource, idx) => (
      <PinnedResource
        key={`${resource}_${idx.toString()}`}
        idx={idx}
        resourceRef={resource}
        onChange={setPinnedResources}
        onReorder={setValidPinnedResources}
        onDrag={setIsDragged}
        navResources={validPinnedResources}
        draggable={validPinnedResources.length > 1}
      />
    ));

  const NavGroupWithDnd = withDragDropContext(() => (
    <NavGroup
      title=""
      aria-label={t('console-app~Pinned resources')}
      className={css('no-title', { 'oc-perspective-nav--dragging': isDragged })}
    >
      {getPinnedItems()}
    </NavGroup>
  ));

  // We have to use NavList if there is at least one extension that will render an <li>, but we
  // can't use NavList if there are no extensions that render an <li>
  const hasListItem = orderedNavExtensions.some(
    (item) => (isNavSection(item) && item.properties.name) || isNavItem(item),
  );

  const content = (
    <>
      {orderedNavExtensions.map((extension) => (
        <PluginNavItem key={extension.uid} extension={extension} />
      ))}
      {pinnedResourcesLoaded && validPinnedResources?.length > 0 ? <NavGroupWithDnd /> : null}
    </>
  );

  return hasListItem ? (
    <NavList
      className="oc-perspective-nav"
      title=""
      aria-label={t('console-app~Main navigation')}
      data-test-id={`${activePerspective}-perspective-nav`}
    >
      {content}
    </NavList>
  ) : (
    <div className="oc-perspective-nav" data-test-id={`${activePerspective}-perspective-nav`}>
      {content}
    </div>
  );
};

export default PerspectiveNav;
